/**
 * Canvas Theme - Cloud-Inspired Glassmorphism Design
 * Ultra-transparent, minimal aesthetic with soft edges and ethereal feeling
 *
 * Design principles:
 * - Glass at different densities over a living bioluminescent surface
 * - Single accent family: #667eea (indigo) -> #764ba2 (amethyst)
 * - All shadows tinted by the floor's indigo light
 * - One border opacity for all glass: rgba(255, 255, 255, 0.18)
 * - Three motion types: appear (spring), shift (smooth), float (slow decel)
 */

export const CANVAS_THEME = {
  // Background gradients
  canvasBackground: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',

  // Panel styling - CLOUD-INSPIRED
  // Ultra transparent (0.45) for ethereal cloud feeling
  // Softer edges (28px), minimal borders, enhanced blur
  panelBackground: 'rgba(255, 255, 255, 0.45)',
  panelBackdrop: 'blur(32px)',
  panelBorder: '1px solid rgba(255, 255, 255, 0.18)',
  panelShadow: '0 8px 32px rgba(31, 38, 135, 0.08)',
  panelBorderRadius: '28px',
  panelTitleBackground: 'rgba(255, 255, 255, 0.25)',

  // Hover states - subtle lift
  panelHoverShadow: '0 12px 40px rgba(31, 38, 135, 0.12)',
  panelHoverScale: 1.005, // Minimal scale for cloud softness

  // Grid
  gridSize: 20, // Snap grid cell size (px)
  gridColor: 'rgba(255, 255, 255, 0.05)', // Much more subtle
  gridLineWidth: 1,

  // Colors - unified accent family
  textPrimary: '#1f1f2e',
  textSecondary: '#666666',
  accentColor: '#667eea',
  accentColorDark: '#5a6fd6', // Darker shade for active/hover states
  accentColorLight: '#a5b4fc',
  accentGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',

  // Accent as rgba for transparency variants
  accentRgba: (alpha: number) => `rgba(102, 126, 234, ${alpha})`,

  // Toolbar - translucent glass (denser than panels)
  toolbarBackground: 'rgba(255, 255, 255, 0.55)',
  toolbarBackdrop: 'blur(24px)',
  toolbarBorder: '1px solid rgba(255, 255, 255, 0.18)',
  toolbarShadow: '0 4px 20px rgba(31, 38, 135, 0.1)',

  // Z-index layers
  zIndex: {
    grid: 1,
    panels: 100,
    toolbar: 99000, // High z-index so dropdown appears above all panels
    dropdown: 99999, // Highest - always on top
    modal: 100000,
  },

  // Transitions - three distinct motion types
  // Appear: spring with slight overshoot, for panels entering
  transitionAppear: '0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
  // Shift: smooth ease, for hover/focus state changes
  transitionShift: '0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  // Float: slow deceleration, for drag release and settling
  transitionFloat: '0.4s cubic-bezier(0.16, 1, 0.3, 1)',

  // Legacy (kept for backward compat, maps to shift)
  transitionDuration: '0.2s',
  transitionTiming: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',

  // Compact spacing (minimal padding)
  panelPaddingX: '12px',
  panelPaddingY: '10px',
  titlePaddingX: '14px',
  titlePaddingY: '8px',

  // Shadows - all indigo-tinted (colored by floor's light)
  shadowLight: '0 4px 20px rgba(31, 38, 135, 0.08)',
  shadowMedium: '0 8px 32px rgba(31, 38, 135, 0.12)',
  shadowHeavy: '0 20px 60px rgba(31, 38, 135, 0.2)',
  shadowModal: '0 20px 60px rgba(31, 38, 135, 0.25)',
};

export type CanvasTheme = typeof CANVAS_THEME;
